import React, { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { reduxForm } from 'redux-form';
import { useHistory, useParams } from 'react-router-dom';
import { Title } from '@app/src/common/Title';
import {
  Description,
  TypeOperation,
  TypeProperty,
  TitleDistrict,
  FormActions
} from '@app/src/views/StepOne/form';
import { FormWrapper } from './styled';
import { setPrintGeneral, GeneralModel, deleteExtrachargeData, clearTypePublicationAll } from '@app/src/views/StepTwo/channelPublication';
import { isClientEnv } from '@app/src/utils/validations';
import { clearDistrictFullData } from '@app/src/views/StepOne/form';
import { CATEGORIES } from '@app/src/config/categories';
import { cleanPrintGeneral } from '@app/src/views/StepTwo/channelPublication';
import { COLORS } from '@app/src/ui';

interface Props {
  handleSubmit?: Function;
}

const isClient = isClientEnv();
const COLOR_ACTIVE = COLORS.BLUE_LINK;

const Form: React.FC<Props> = ({ handleSubmit }) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const { packageId } = useParams();

  const currentOperation = useSelector(state => state.form.stepOne.values.operation);
  const currentOperationName = useSelector(state => state.form.stepOne.values.operationName);
  const currentProperty = useSelector(state => state.form.stepOne.values.property);
  const currentPropertyName = useSelector(state => state.form.stepOne.values.propertyName);
  const currentTitle = useSelector(state => state.form.stepOne.values.title);
  const currentDescription = useSelector(state => state.form.stepOne.values.description);
  const currentUbigeo = useSelector(state => state.district);
  const currentDistrictName = useSelector(state => state.form.stepOne.values.districtName);
  const print = isClient && JSON.parse(sessionStorage.getItem('print'));
  const typeUserAction = useSelector(state => state.session.user.pasarella);

  useEffect(() => {
    if (currentDescription && isClient) {
      window.addEventListener('beforeunload', handleBeforeUnload);
      return () => {
        window.removeEventListener('beforeunload', handleBeforeUnload);
      }
    }
  }, [currentDescription]);

  const handleBeforeUnload = (e) => {
    if (isClient) {
      sessionStorage.setItem('print', JSON.stringify({ general: { category: 1 } }));
      e.returnValue = '¿Desea descartar cambios?';
    }
  };

  const handleClickSubmit = (data: GeneralModel) => {
    dispatch(deleteExtrachargeData());
    dispatch(clearTypePublicationAll());
    const updatePrint = createPrintStorage(data);
    isClient && sessionStorage.setItem('print', JSON.stringify(updatePrint));
    dispatch(setPrintGeneral(updatePrint.general));
    pushDatalayer(
      CATEGORIES.inmueble.name,
      currentOperationName,
      currentPropertyName,
      currentDistrictName
    );
    if (typeUserAction === 'consume-print') {
      history.push(CATEGORIES.inmueble.paths.consumePackage.stepTypePublication + (packageId && `/${packageId}`));
    }
    if (typeUserAction === 'purchase-print') {
      history.push(CATEGORIES.inmueble.paths.purchasePrint.stepTypePublication);
    }
  }

  const quitLastSpace = (text) => {
    let arrText = text.split(' ');
    if (arrText[arrText.length - 1].length === 0) {
      arrText.pop();
    }
    return arrText.join(' ');
  }

  const createPrintStorage = (data) => {
    const objPrint = {};
    if (currentUbigeo && currentUbigeo.hasOwnProperty('ubigeo')) {
      data['ubigeo'] = currentUbigeo.ubigeo;
    } else {
      dispatch(clearDistrictFullData());
    }
    if (print && print.hasOwnProperty('printsByChannel')) {
      objPrint['printsByChannel'] = print.printsByChannel;
    } else {
      objPrint['printsByChannel'] = {};
    }
    data.description = quitLastSpace(data.description);
    objPrint['general'] = data;
    return objPrint;
  }

  const handleClickReset = (e) => {
    e.preventDefault();
    if (currentDescription) {
      const isDiscardChanges = confirm('¿desea descartar cambios?');
      if (!isDiscardChanges) {
        return;
      }
    }
    history.goBack();
    sessionStorage.removeItem('print');
    dispatch(cleanPrintGeneral());
    dispatch(clearDistrictFullData());
  }

  const pushDatalayer = (categoryName: string, operationName: string, propertyName: string, district: string) => {
    if (isClient && (window as any).dataLayer) {
      (window as any).dataLayer.push({
        'event': 'EVClasificados',
        'eventCategory': 'Posting',
        'eventAction': `Paso1 - ${categoryName}`,
        'eventLabel': `${operationName}|${propertyName}${district && `|${district}`}`,
        'eventValue': 1
      })
    }
  }

  return (
    <FormWrapper>
      <Title description="Ingrese los datos de contacto en la descripción del aviso.">Datos del aviso</Title>
      <TypeOperation activeColor={COLOR_ACTIVE} />
      {currentOperation && <TypeProperty operation={currentOperation} activeColor={COLOR_ACTIVE} />}
      {
        (currentOperation && currentProperty) &&
        <TitleDistrict propertyId={currentProperty} activeColor={COLOR_ACTIVE} />
      }
      {
        currentOperation && currentProperty && currentTitle &&
        <Description
          includeWords={12}
          extraWords={(typeUserAction === 'purchase-print') ? 8 : 0}
        />
      }
      <FormActions
        showButtonNext={currentOperation && currentProperty && currentTitle}
        handleButtonNext={handleSubmit(handleClickSubmit)}
        handleButtonPrev={handleClickReset}
      />
    </FormWrapper>
  );
}

const FormHoc = reduxForm({
  form: 'stepOne',
  initialValues: {
    category: null,
    title: '',
    description: '',
    property: null,
    operation: null,
    operationName: '',
    propertyName: '',
    districtName: '',
    extraWords: null,
  }
})(Form);

export { FormHoc as Form };
